<template>
  <mi-skeleton
    v-if="areAllOptionsLoading"
    class="search-results-group"
    type="text"
    height="44px"
    width="398px"
    animation="wave"
  ></mi-skeleton>
  <mi-expansion-item
    v-else
    v-model="isOpened"
    :class="{
      'search-results-group--removable': removable,
      [`search-results-group--${ color }`]: true,
    }"
    class="search-results-group"
    header-class="items-center"
    dense
  >
    <!-- Header -->
    <template #header>
      <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -->
      <div
        class="search-results-group__header-wrapper row items-center no-wrap full-width"
        tabindex="-1"
        @mouseenter="isHeaderHovered = true"
        @mouseleave="isHeaderHovered = false"
        @focusin="isHeaderHovered = true"
        @focusout="onHeaderFocusOut"
      >
        <mi-list-item-section v-if="!pacRestorePoint && !removable" class="search-results-group__action" side>
          <!-- Checkbox -->
          <mi-checkbox
            data-cy="search-results-group-item"
            :model-value="isItemSelected"
            @click.stop="handleItemClick(elementTypes, { item, isChosen: isItemSelected })"
          ></mi-checkbox>
        </mi-list-item-section>

        <div
          v-if="hasSelection && !removable && isHeaderHovered && showSelectionToggle"
          class="search-results-group__counter row items-center no-wrap q-mr-sm"
          @click.stop.prevent
        >
          <span class="search-results-group__count">{{ selectedSubItemsCount }}/{{ totalSubItemsCount }}</span>
          <div class="search-results-group__toggle">
            <button
              class="search-results-group__toggle-btn search-results-group__toggle-btn--left"
              :class="{ 'search-results-group__toggle-btn--active': isPartialSelection }"
              @click.stop.prevent="toggleFullPartial(false)"
            >
              Partial
            </button>
            <button
              class="search-results-group__toggle-btn search-results-group__toggle-btn--right"
              :class="{ 'search-results-group__toggle-btn--active': isFullSelection }"
              @click.stop.prevent="toggleFullPartial(true)"
            >
              Full
            </button>
          </div>
        </div>

        <!-- Title -->
        <search-results-item-title
          v-show="!isHeaderHovered || !hasSelection || removable || !showSelectionToggle"
          :code-id="item.id2"
          :name="item.name"
          :persistent="persistentTitle"
        ></search-results-item-title>

        <span v-if="!pacRestorePoint && removable" class="search-results-group-item__counter">
          ({{ item[elementTypes.subItem].length }}/{{ totalSubElementsCounter }})
        </span>

        <span v-if="activateItemCheckBoxOnlyWhenAllSelected" class="text-body3 text-weight-bold">
          {{ selectedElementsCounter }}
        </span>

        <mi-list-item-section v-if="!pacRestorePoint && removable" class="search-results-group__action" side>
          <div>
            <!-- Remove icon -->
            <mi-btn
              :ripple="false"
              class="search-results-group__remove-btn"
              icon="trash"
              dense
              fab
              flat
              icon-type
              @click.stop="handleItemClick(elementTypes, { item, isChosen: true })"
            ></mi-btn>
            <mi-tooltip anchor="top middle" self="bottom middle"> Remove </mi-tooltip>
          </div>
        </mi-list-item-section>
        <mi-list-item-section side>
          <slot name="custom-right-header"></slot>
        </mi-list-item-section>
      </div>
    </template>

    <!-- Content -->
    <mi-list separator>
      <transition-group
        name="fade-sub-item"
        tag="mi-list"
        separator
      >
        <mi-list-item
          v-for="(subItem) in item[elementTypes.subItem]"
          :key="subItem.id"
          :tag="removable ? 'div' : 'label'"
          class="search-results-group__item items-center q-pl-xl"
          :class="removable?
            '': 'q-pl-xl'"
          @click="handleSubItemClick(elementTypes, { subItem, isChosen: isSubItemSelected(subItem) })"
        >
          <mi-list-item-section v-if="!pacRestorePoint" class="search-results-group__action" side>
            <!-- Checkbox -->
            <mi-checkbox
              v-if="!removable"
              :model-value="isSubItemSelected(subItem)"
              class="no-pointer-events"
            ></mi-checkbox>
          </mi-list-item-section>

          <!-- Title -->
          <search-results-item-title
            :code-id="subItem.id2"
            :name="subItem.name"
            :persistent="persistentTitle"
          ></search-results-item-title>

          <mi-list-item-section v-if="!pacRestorePoint && removable" class="search-results-group__action" side>
            <div>
              <mi-btn
                v-if="color=== ELEMENT_COLORS().GREEN"
                :riple="false"
                icon="plus"
                dense
                fab
                flat
                icon-type
                class="search-results-group__add-all-options-btn"
                @click.stop="handleAddAllOptionsBtnClick(subItem)"
              ></mi-btn>
              <mi-tooltip anchor="top left" self="bottom middle"> Add all options </mi-tooltip>
            </div>

            <!-- Remove icon -->
            <div>
              <mi-btn
                :ripple="false"
                class="search-results-group__remove-btn"
                icon="trash"
                dense
                fab
                flat
                icon-type
                @click.stop="handleSubItemClick(elementTypes, { subItem, isChosen: true })"
              ></mi-btn>
              <mi-tooltip anchor="top middle" self="bottom middle"> Remove </mi-tooltip>
            </div>
          </mi-list-item-section>
        </mi-list-item>
      </transition-group>
    </mi-list>
  </mi-expansion-item>
</template>

<script>
  import { ELEMENT_COLORS, SEARCH_RESULT_TYPES } from '@/constants'
  import MiListItemSection from '@/packages/@mi-library/MiList/MiListItemSection.vue'
  import SearchResultsItemTitle from './SearchResultsItemTitle.vue'
  import MiBtn from '../../../packages/@mi-library/MiBtn/MiBtn.vue'
  import MiTooltip from '../../../packages/@mi-library/MiTooltip/MiTooltip.vue'
  import MiSkeleton from '../../../packages/@mi-library/MiSkeleton/MiSkeleton.vue'

  export default {
    name: 'SearchResultsGroup',
    components: { MiListItemSection, MiSkeleton, MiTooltip, MiBtn, SearchResultsItemTitle },
    props: {
      color: {
        type: String,
        required: false,
        default: ELEMENT_COLORS.YELLOW,
        validator: value => Object.values(ELEMENT_COLORS).includes(value)
      },
      item: {
        type: Object,
        required: true
      },
      persistentTitle: {
        type: Boolean,
        required: false,
        default: false
      },
      pacRestorePoint: {
        type: Boolean,
        required: false,
        default: false
      },
      totalSubElementsCounter: {
        type: Number,
        required: false,
        default: 0
      },
      defaultOpened: {
        type: Boolean,
        required: false,
        default: false
      },
      opened: {
        type: Boolean,
        required: false,
        default: true
      },
      removable: {
        type: Boolean,
        required: false,
        default: false
      },
      activateItemCheckBoxOnlyWhenAllSelected: {
        type: Boolean,
        required: false,
        default: false
      },
      isElementFullySelected: {
        type: Function,
        required: false,
        default: () => {}
      },
      selectedElementsLength: {
        type: Function,
        required: false,
        default: () => {}
      },
      selectedElementsIds: {
        type: Object,
        required: false,
        default: () => ({})
      },
      handleItemClick: {
        type: Function,
        required: false,
        default: () => {}
      },
      handleSubItemClick: {
        type: Function,
        required: false,
        default: () => {}
      },
      addAllOptions: {
        type: Function,
        required: false,
        default: () => {}
      },
      areAllOptionsLoading: {
        type: Boolean,
        required: false,
        default: false
      },
      totalSubItemsCountMap: {
        type: Object,
        required: false,
        default: () => ({})
      },
      allSubItemsMap: {
        type: Object,
        required: false,
        default: () => ({})
      },
      onToggleFullSelection: {
        type: Function,
        required: false,
        default: () => {}
      },
      showSelectionToggle: {
        type: Boolean,
        required: false,
        default: false
      }
    },
    emits: ['update:opened'],
    data() {
      return {
        isHeaderHovered: false,
        isOpened: this.opened ?? true
      }
    },
    computed: {
      elementTypes() {
        const { OPTIONS, CHOICES, COMPONENTS, COMPONENT_VARIANTS } = SEARCH_RESULT_TYPES
        let item = ''
        let subItem = ''

        if (this.color === ELEMENT_COLORS.YELLOW) {
          item = OPTIONS
          subItem = CHOICES
        }
        else if (this.color === ELEMENT_COLORS.GREEN) {
          item = COMPONENTS
          subItem = COMPONENT_VARIANTS
        }

        return { item, subItem }
      },

      currentSelectedElementsIds() {
        return this.selectedElementsIds[this.elementTypes.item] || new Map()
      },

      isItemSelected() {
        if (this.activateItemCheckBoxOnlyWhenAllSelected) {
          return this.isElementFullySelected(
            this.item, this.elementTypes, this.currentSelectedElementsIds
          )
        }

        return [...this.currentSelectedElementsIds.keys()].includes(this.item.id)
      },

      selectedElementsCounter() {
        return this.selectedElementsLength(
          this.item, this.elementTypes, this.currentSelectedElementsIds
        )
      },
      totalSubItemsCount() {
        return this.totalSubItemsCountMap[this.item.id] || this.item[this.elementTypes.subItem]?.length || 0
      },
      selectedSubItemsCount() {
        return this.currentSelectedElementsIds?.get(this.item.id)?.length || 0
      },
      isFullSelection() {
        return this.selectedSubItemsCount > 0 && this.selectedSubItemsCount === this.totalSubItemsCount
      },
      isPartialSelection() {
        return this.selectedSubItemsCount > 0 && this.selectedSubItemsCount < this.totalSubItemsCount
      },
      hasSelection() {
        return this.selectedSubItemsCount > 0
      }
    },
    watch: {
      opened(value) {
        this.isOpened = value
      },
      isOpened(value) {
        this.$emit('update:opened', { id: this.item.id, opened: value })
      }
    },
    methods: {
      ELEMENT_COLORS() {
        return ELEMENT_COLORS
      },
      isSubItemSelected(subItem = {}) {
        return this.removable
          ? false
          : !!this.currentSelectedElementsIds.get(this.item.id)?.includes(subItem.id)
      },
      handleAddAllOptionsBtnClick(item) {
        this.addAllOptions({ singleComponentVariant: true, componentVariantId: item.id })
      },
      toggleFullPartial(isFull) {
        this.onToggleFullSelection({
          item: this.item,
          elementTypes: this.elementTypes,
          isFull
        })
      },
      onHeaderFocusOut(event) {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          this.isHeaderHovered = false
        }
      }
    }
  }
</script>

<style lang="scss" scoped>
  .fade-sub-item-enter-active,
  .fade-sub-item-leave-active {
    transition: opacity .4s ease;
  }

  .fade-sub-item-enter-from,
  .fade-sub-item-leave-to {
    opacity: 0;
  }

  .search-results-group {
    font-size: .875rem;

    ::v-deep(.mi-list-item) {
      border: 1px solid $mi-white;
    }

    ::v-deep(.mi-expansion-item__header) {
      border-bottom: 1px solid transparent;
      position: relative;
      z-index: 0;

      .q-expansion-item__toggle-icon {
        font-size: .5rem;
      }
    }

    &.q-expansion-item--expanded ::v-deep(.mi-expansion-item__header) {
      border-bottom-color: $mi-silver-200;
    }

    .search-results-group__action:not(.mi-expansion-item__expand-icon) {
      display: flex;
      flex-direction: row;
      padding-right: .5rem;
    }

    .q-expansion-item__content .search-results-group__action {
      display: flex;
      flex-direction: row;
      margin-left: -.25rem;
    }

    &__add-all-options-btn.mi-btn {
      display: none;
    }

    &__remove-btn.mi-btn {
      font-size: 1rem;
      margin-bottom: -1px;
      display: none;
    }

    &__item:last-child {
      box-shadow: 0 1px 0 $mi-silver-200;
    }
  }

  .search-results-group--yellow ::v-deep(.mi-expansion-item__header) {
    background-color: $mi-yellow-200;
  }

  .search-results-group--yellow ::v-deep(.mi-expansion-item__header):hover {
    background-color: #e7deaf;

    .search-results-group__remove-btn.mi-btn {
      display: block;
    }

    .search-results-group-item__counter {
      display: none;
    }
  }

  .search-results-group--yellow ::v-deep(.mi-list-item) {
    background-color: $mi-yellow-50;
  }

  .search-results-group--yellow ::v-deep(.mi-list-item):hover {
    background-color: #eae7de;

    .search-results-group__remove-btn.mi-btn {
      display: block;
    }

    .search-results-group-item__counter {
      display: none;
    }
  }

  .search-results-group--green ::v-deep(.mi-expansion-item__header) {
    background-color: $mi-green-200;
  }

  .search-results-group--green ::v-deep(.mi-expansion-item__header):hover {
    background-color: #cfd7b0;

    .search-results-group__remove-btn.mi-btn {
      display: block;
    }

    .search-results-group__add-all-options-btn.mi-btn {
      display: block;
    }

    .search-results-group-item__counter {
      display: none;
    }
  }

  .search-results-group--green ::v-deep(.mi-list-item) {
    background-color: $mi-green-50;
  }

  .search-results-group--green ::v-deep(.mi-list-item):hover {
    background-color: #e6ead7;

    .search-results-group__remove-btn.mi-btn {
      display: block;
    }

    .search-results-group__add-all-options-btn.mi-btn {
      display: block;
    }

    .search-results-group-item__counter {
      display: none;
    }
  }

  .search-results-group--removable .mi-expansion-item__header .search-results-group__action {
    margin-left: -.5rem;
  }

  .search-results-group--removable .q-expansion-item__content .search-results-group__action {
    margin-left: -.75rem;
  }

  .search-results-group--removable .search-results-group__action:not(.mi-expansion-item__expand-icon) {
    padding-right: .25rem;
  }

  .search-results-group__counter {
    white-space: nowrap;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 32px;
    position: relative;
    font-size: 14px;
  }

  .search-results-group__count {
    font-size: 14px;
    font-weight: 700;
    color: $mi-anthracite-800;
  }

  .search-results-group__toggle {
    display: flex;
    border-radius: 20px;
    overflow: hidden;
    background-color: $mi-white;
    padding: 2px;
    gap: 2px;
  }

  .search-results-group__toggle-btn {
    font-family: inherit;
    font-size: 14px;
    font-weight: 700;
    padding: .125rem .625rem;
    border: 0;
    border-radius: 20px;
    cursor: pointer;
    background-color: transparent;
    color: $mi-anthracite-800;
    line-height: 1.4;

    &--active {
      background-color: $mi-yellow-400;
    }
  }

  .search-results-group ::v-deep(.q-expansion-item__content) {
    overflow: hidden;
  }

  .search-results-group__header-wrapper {
    gap: 10px;
  }

  .search-results-group-item {
    &__counter {
      font-weight: 700;
      margin-left: 23px;
    }
  }
</style>
